/**
 * Composable de FILTRADO POR ASIGNACIÓN.
 *
 * Resuelve una pregunta que se repite en varias vistas: ¿este usuario debe ver
 * todo el proceso, o solo los indicadores que tiene asignados?
 *
 * Un administrador o un responsable de proceso ve todo; un evaluador solo lo
 * suyo. Centralizarlo evita que cada vista reimplemente la regla —y la
 * reimplemente distinto—.
 *
 * ⚠️ Es filtrado de PRESENTACIÓN, no de seguridad. Sirve para no mostrar ruido
 * irrelevante; el control de acceso real lo aplica el backend en cada petición.
 */

import { computed, onMounted } from 'vue'
import useAutenticar from '@/store/IngresoAutenticar'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'

/**
 * Expone las asignaciones del usuario actual y si sus vistas deben filtrarse.
 *
 * Carga las asignaciones en `onMounted` y solo si el store está vacío, de modo
 * que varias vistas que usen el composable en la misma sesión comparten una única
 * petición.
 *
 * `necesitaFiltro` es `true` cuando el usuario NO es admin y NO puede crear
 * procesos: ese es el perfil de evaluador, que solo debe ver lo asignado.
 *
 * ── Casos borde ──────────────────────────────────────────────────
 *
 *  - Sin usuario en sesión no se carga nada y los arrays quedan vacíos, lo que
 *    para un usuario con `necesitaFiltro` significa "no ve nada" — el fallo
 *    seguro.
 *  - `idsIndicadores` descarta los null: son las asignaciones de responsable de
 *    proceso, que no apuntan a un indicador concreto.
 *  - Una asignación con `id_ejecucion` null (legacy, previa a ligar las
 *    asignaciones a una ejecución) se considera válida para CUALQUIER ejecución
 *    de esa versión, para no ocultar trabajo antiguo.
 *  - Los ids se normalizan con `Number()` porque el API puede devolverlos como
 *    string y una comparación estricta fallaría en silencio.
 *
 * @returns `necesitaFiltro`, `idsVersiones`, `idsIndicadores` y
 *          `tieneAsignacionProceso(idVersion, idEjecucion)`.
 *
 * @example
 * const { necesitaFiltro, idsIndicadores } = useAsignacionActual()
 * const visibles = computed(() =>
 *   necesitaFiltro.value
 *     ? indicadores.value.filter(i => idsIndicadores.value.includes(i.id_indicador))
 *     : indicadores.value
 * )
 */
export function useAsignacionActual() {
  const auth = useAutenticar()
  const store = useGestionIndicadorEvaluadores()

  onMounted(async () => {
    const id = auth.usuarioActual?.id
    if (id && store.misAsignaciones.length === 0) {
      await store.cargarMisAsignaciones(id)
    }
  })

  // True cuando el usuario solo debe ver datos que tiene asignados
  const necesitaFiltro = computed(() =>
    !auth.esAdmin && !auth.tienePermiso('procesos.crear')
  )

  const idsVersiones = computed(() =>
    [...new Set(store.misAsignaciones.map(a => a.id_version))]
  )

  const idsIndicadores = computed(() =>
    store.misAsignaciones
      .map(a => a.id_indicador)
      .filter((id): id is number => id != null)
  )

  // Retorna true si el usuario tiene asignación para un proceso concreto.
  // Usa Number() para normalizar porque la API puede devolver IDs como strings.
  function tieneAsignacionProceso(idVersion: number, idEjecucion: number): boolean {
    return store.misAsignaciones.some(
      a => Number(a.id_version) === idVersion &&
           (Number(a.id_ejecucion) === idEjecucion || a.id_ejecucion == null)
    )
  }

  return { necesitaFiltro, idsVersiones, idsIndicadores, tieneAsignacionProceso }
}
